import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio } from 'lucide-react';
import { MarketingChannelSummaryItem } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const monthStart = () => {
  const n = new Date();
  return fmtDate(new Date(n.getFullYear(), n.getMonth(), 1));
};
const monthEnd = () => {
  const n = new Date();
  return fmtDate(new Date(n.getFullYear(), n.getMonth() + 1, 0));
};

const MarketingChannels: React.FC = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(monthEnd());
  const [items, setItems] = useState<MarketingChannelSummaryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activelyUsing, setActivelyUsing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getMarketingChannelSummary(startDate, endDate, activelyUsing);
      setItems(res.data);
      setTotal(res.meta.totalCompanies);
    } catch (e) {
      console.error('Error loading marketing channel summary:', e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, activelyUsing]);

  useEffect(() => {
    load();
  }, [load]);

  const maxCount = items.reduce((m, i) => Math.max(m, i.companiesCount), 0) || 1;

  const goToCompanies = (item: MarketingChannelSummaryItem) => {
    const channelParam = item.type === 'channel' ? item.id : item.type; // uuid | 'other' | 'none'
    if (!channelParam) return;
    const params = new URLSearchParams({
      marketingChannelId: channelParam,
      marketingChannelName: item.name,
      startDate,
      endDate,
      page: '1',
    });
    if (activelyUsing) {
      params.set('isActivelyUsing', '1');
    }
    navigate(`/companies?${params.toString()}`);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Radio className="h-6 w-6 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">Marketing Channels</h1>
      </div>

      <div className="mb-6 bg-white p-4 rounded-lg shadow flex flex-col sm:flex-row gap-4 sm:items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2 sm:pb-2">
          <input
            type="checkbox"
            id="activelyUsing"
            checked={activelyUsing}
            onChange={(e) => setActivelyUsing(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="activelyUsing" className="text-sm font-medium text-gray-700 cursor-pointer">
            Sedang aktif dipakai
          </label>
        </div>
        <div className="sm:ml-auto text-right">
          <div className="text-sm text-gray-500">Total companies</div>
          <div className="text-2xl font-bold text-gray-900">{total.toLocaleString()}</div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {items.map((item, idx) => {
            const isPseudo = item.type !== 'channel';
            return (
              <button
                key={item.id ?? item.type}
                onClick={() => goToCompanies(item)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-4"
              >
                <span className="w-6 text-sm text-gray-400">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-medium truncate ${isPseudo ? 'text-gray-500 italic' : 'text-gray-900'}`}>
                      {item.name}
                    </span>
                    <span className="ml-3 inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full text-sm font-semibold bg-primary-100 text-primary-700">
                      {item.companiesCount}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={isPseudo ? 'h-full bg-gray-300' : 'h-full bg-primary-500'}
                      style={{ width: `${(item.companiesCount / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              </button>
            );
          })}
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500">No data for this period.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketingChannels;
